import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import type {Dispatch, MutableRefObject, SetStateAction} from 'react';
// @ts-expect-error
import type {Control, UseFormGetValues, UseFormSetValue, Watch} from 'react-hook-form';
// @ts-expect-error
import {useFieldArray, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {FlashBanner} from 'shells/flash-banner/flash-banner';

import type {FormPageFragment, FormPageRuleInput} from 'nutshell-graphql-types';

import {getFormSchema} from '../../../forms-selectors';
import {getFieldDisplayType} from '../../../helpers';
import type {ActiveField} from '../../../types';

import type {FormSchemaProperty} from '../../../../core/nutshell-fields/types';

import {FONT_OPTIONS} from '../../editor/fonts/constants';
import {fetchFormFont} from '../../editor/fonts/form-font-helpers';
import {updateRemainingPersonEmailFields} from '../../editor/helpers';

import type {PageFieldArrayOps} from '../drag-and-drop/types';
import {useUpdateFormPageRules} from '../graphql/hooks/use-update-form-page-rules';
import {createDefaultSubmitButtonField} from '../helpers';
import {
    formFieldRowToSelectedExistingField,
    isFormFieldInputRow,
    resolveCustomFieldViewForDroppedExistingField,
} from '../sidebar/add-custom-field/helpers';
import type {
    EditCustomFieldSession,
    NewCustomFieldFormPayload,
} from '../sidebar/add-custom-field/types';
import type {CustomFieldView} from '../sidebar/blocks-and-design/content-blocks/types';
import {Sidebar, type SidebarState} from '../sidebar/sidebar';
import type {DisplayType, Field, FormPage, FormPageWithRowKey, MultiPageForm} from '../types';

import {getGraphqlPageForCanvasRow} from './helpers';
import {PageDefaultRuleEditor} from './page-default-rule-editor';
import {FormEditorPage} from './preview/form-editor-page';
import {useSyncPageFieldOpsRef} from './use-sync-page-field-ops-ref';

import styles from './form-editor.module.css';

interface FormEditorProps {
    formId: string;
    formHeader?: React.ReactNode;
    dndPageFieldMutatorsRef: MutableRefObject<Map<number, PageFieldArrayOps>>;
    control: Control;
    watch: Watch;
    getValues: UseFormGetValues<MultiPageForm>;
    fieldCreateAtIndex: {pageIndex: number; insertIndex: number} | null;
    saveFailedErrorMessage: string | null | undefined;
    onClearError: () => void;
    minimumOneEmailField?: boolean;
    graphqlPages?: FormPageFragment[] | null;
    sidebarState: SidebarState;
    setSidebarState: (state: SidebarState) => void;
    customFieldView: CustomFieldView | null;
    editCustomFieldSession: EditCustomFieldSession | null;
    setEditCustomFieldSession: Dispatch<SetStateAction<EditCustomFieldSession | null>>;
    openEditCustomFieldSession: (session: EditCustomFieldSession) => void;
    setValue: UseFormSetValue<MultiPageForm>;
    onPickExistingSchemaFieldForForm: (field: FormSchemaProperty) => void;
    onSubmitNewField: (payload: NewCustomFieldFormPayload) => Promise<void>;
}

export function FormEditor(props: FormEditorProps) {
    const {
        formId,
        control,
        watch,
        getValues,
        dndPageFieldMutatorsRef,
        fieldCreateAtIndex,
        saveFailedErrorMessage,
        onClearError,
        minimumOneEmailField,
        formHeader,
        graphqlPages,
    } = props;

    const {updateFormPageRules} = useUpdateFormPageRules();

    const [activeField, setActiveField] = useState<ActiveField | null | undefined>(null);
    const [activePageIndex, setActivePageIndex] = useState<number | null | undefined>(null);

    const {
        sidebarState,
        setSidebarState,
        customFieldView,
        editCustomFieldSession,
        setEditCustomFieldSession,
        openEditCustomFieldSession,
        setValue,
        onPickExistingSchemaFieldForForm,
        onSubmitNewField,
    } = props;

    const formSchema = useSelector(getFormSchema);

    const {
        fields: formPages,
        append: appendPage,
        update: updatePage,
        remove: removePageFromArray,
    } = useFieldArray({
        control,
        name: 'pages',
        keyName: 'key',
    });

    const watchedPages = useWatch({control, name: 'pages'});

    const allFormFields = useMemo(
        () => (watchedPages ?? []).flatMap((page: FormPageWithRowKey) => page.fields ?? []),
        [watchedPages]
    );

    useSyncPageFieldOpsRef({
        dndPageFieldMutatorsRef,
        getValues,
        updatePage,
        watchedPages,
    });

    const addFormPage = useCallback(() => {
        const newPage: FormPage = {id: null, fields: [createDefaultSubmitButtonField()]};
        appendPage(newPage);
    }, [appendPage]);

    const setActiveFieldAndSidebar = useCallback(
        (input?: ActiveField | null) => {
            setActiveField(input);
            if (!input) {
                setEditCustomFieldSession(null);
                setSidebarState('blocks-and-design');
                return;
            }

            const ctx = input.customFieldEditContext;
            if (ctx && formSchema) {
                const pages = getValues('pages');
                const row = pages?.[ctx.pageIndex]?.fields?.[ctx.fieldIndex] as Field | undefined;
                if (row && isFormFieldInputRow(row)) {
                    const fieldDisplayType = getFieldDisplayType(formSchema, {
                        ...row,
                        type: row.type,
                    });
                    const view = resolveCustomFieldViewForDroppedExistingField(
                        fieldDisplayType as DisplayType,
                        row
                    );

                    if (view) {
                        openEditCustomFieldSession({
                            view,
                            existingField: formFieldRowToSelectedExistingField(row),
                            editedFieldRef: {
                                pageIndex: ctx.pageIndex,
                                fieldIndex: ctx.fieldIndex,
                            },
                        });
                        return;
                    }
                }
            }
            setEditCustomFieldSession(null);
            setSidebarState('block-editor');
        },
        [
            formSchema,
            getValues,
            openEditCustomFieldSession,
            setEditCustomFieldSession,
            setSidebarState,
        ]
    );

    const pageTargetChoices = useMemo(() => {
        if (!graphqlPages?.length) {
            return [];
        }
        return graphqlPages
            .map((p, idx) => ({
                id: p.id ?? '',
                label: `Page ${p.position ?? idx + 1}`,
            }))
            .filter((p) => p.id);
    }, [graphqlPages]);

    const persistDefaultRule = useCallback(
        (pageApiId: string, rule: FormPageRuleInput) =>
            updateFormPageRules({
                pageId: pageApiId,
                defaultRule: rule,
            }),
        [updateFormPageRules]
    );

    const removePage = useCallback(
        (pageIndex: number) => {
            const pages = getValues('pages') ?? [];
            if (pages.length <= 1) {
                return;
            }
            setActiveField(null);
            removePageFromArray(pageIndex);
        },
        [getValues, removePageFromArray]
    );

    const removePageField = useCallback(
        (pageIndex: number, index: number) => {
            const pages = getValues('pages');
            const page = pages[pageIndex];
            if (!page) {
                return;
            }
            const fieldsCopy = [...(page.fields ?? [])];

            const field = fieldsCopy[index];
            if (
                minimumOneEmailField &&
                field &&
                field.type === 'INPUT' &&
                field.fieldName === 'email' &&
                field.entityType === 'CONTACT'
            ) {
                updateRemainingPersonEmailFields(index, fieldsCopy, (subIdx, updated) => {
                    fieldsCopy[subIdx] = updated;
                });
            }

            setActiveFieldAndSidebar(null);
            const nextFields = fieldsCopy.filter((_, i) => i !== index);
            updatePage(pageIndex, {...page, fields: nextFields});
        },
        [getValues, minimumOneEmailField, setActiveFieldAndSidebar, updatePage]
    );

    useEffect(() => {
        FONT_OPTIONS.forEach((option) => fetchFormFont(option.value.name));
    }, []);

    if (!formSchema) {
        return null;
    }

    const hasMoreThanOnePage = formPages.length > 1;

    return (
        <div className={styles['outer-container']}>
            <div className={styles['left-container']}>
                <div
                    className={styles['canvas-outer']}
                    onClick={() => setActiveFieldAndSidebar(null)}
                >
                    <div className='flex flex-dir-col gap-16 align-center full-width pb-32'>
                        {saveFailedErrorMessage && (
                            <FlashBanner
                                type='error'
                                hasBorder={true}
                                leftAlignContent={true}
                                onHidden={onClearError}
                            >
                                {saveFailedErrorMessage}
                            </FlashBanner>
                        )}
                        {formHeader && (
                            <div className={styles['header-container']}>{formHeader}</div>
                        )}
                        {formPages.map((page: FormPageWithRowKey, pageIndex: number) => {
                            const graphqlPageForRow = getGraphqlPageForCanvasRow(
                                graphqlPages,
                                pageIndex,
                                page
                            );
                            const formPageApiId = page.id ?? graphqlPageForRow?.id ?? null;

                            return (
                                <Fragment key={page.key}>
                                    <FormEditorPage
                                        pageIndex={pageIndex}
                                        fields={page.fields}
                                        control={control}
                                        watch={watch}
                                        onRemoveField={(index) => removePageField(pageIndex, index)}
                                        onRemovePage={removePage}
                                        activeField={activeField}
                                        setActiveField={setActiveFieldAndSidebar}
                                        fieldCreateAtIndex={fieldCreateAtIndex}
                                        formSchema={formSchema}
                                        setSidebarState={setSidebarState}
                                        hasMoreThanOnePage={hasMoreThanOnePage}
                                        setActivePageIndex={setActivePageIndex}
                                    />
                                    {hasMoreThanOnePage && graphqlPages != null && (
                                        <PageDefaultRuleEditor
                                            pageNumber={pageIndex + 1}
                                            formPageId={formPageApiId}
                                            isLastPage={pageIndex === formPages.length - 1}
                                            currentDefaultRule={graphqlPageForRow?.defaultRule}
                                            destinationPages={pageTargetChoices}
                                            onUpdateDefaultRule={(rule: FormPageRuleInput) =>
                                                formPageApiId
                                                    ? persistDefaultRule(formPageApiId, rule)
                                                    : Promise.resolve()
                                            }
                                        />
                                    )}
                                </Fragment>
                            );
                        })}
                        <div
                            className={styles['add-page-container']}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <EmptyAddCard
                                text='+ Add a page'
                                onClick={addFormPage}
                                background='off-white'
                                size='small'
                                withoutIcon={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles['right-container']}>
                <Sidebar
                    formId={formId}
                    sidebarState={sidebarState}
                    setSidebarState={setSidebarState}
                    control={control}
                    watch={watch}
                    getValues={getValues}
                    customFieldView={customFieldView}
                    formFields={allFormFields}
                    editCustomFieldSession={editCustomFieldSession}
                    setEditCustomFieldSession={setEditCustomFieldSession}
                    setValue={setValue}
                    onPickExistingSchemaFieldForForm={onPickExistingSchemaFieldForForm}
                    graphqlPages={graphqlPages}
                    activePageIndex={activePageIndex}
                    onSubmitNewField={onSubmitNewField}
                />
            </div>
        </div>
    );
}
